/* eslint-disable react/no-array-index-key */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { buildParams, parseURL } from '../../global/js/url';
import SearchContext from '../../funnelback-instant-search/jsx/data/FunnelbackContext';

import {
    AssistedInputContainer,
    AssistedInput,
    AssistedInputLabel,
    AssistedInputResultsContainer,
    AssistedInputSourceFunnelback,
} from '@squiz/xaccel-assisted-input';

import generateOrganicSuggestion from './templates/generateOrganicItem';
import generatePeopleSuggestion from './templates/generatePeopleItem';
import generateProgramSuggestion from './templates/generateProgramItem';

import SearchInfoTemplate from './_SearchInfo';

import styles from '../css/functional.module.scss';

/**
 * Search Box Component.
 *
 * @param {string} labelType The version of the label that determines whether to display an icon or text.
 * @param {string} labelText The label text that should be displayed.
 * @param {string} inputName The name attribute of the input field.
 * @param {bool} showClearBtn Flag to determine whether to show the clear button.
 * @param {string} submitType The version of the submit button that determines whether to display an icon or text.
 * @param {string} submitText The text of submit button to render.
 * @param {string} placeholder The default text to render in input field.
 * @param {bool} autofocus Flag to determine whether autofocus on input should be enabled.
 * @param {string} resultPage On submit, send the form-data to the html page to process the input.
 * @param {bool} autocompleteLoader Flag to determine whether to show the loader.
 * @param {string} autocompleteEmphasis Phrase highlight version for autocomplete.
 * @param {array} autocompleteTemplates Array of funnelback autocomplete templates.
 *
 * @returns {JSX.Element}
 * @constructor
 */

function SearchBox({
    labelType,
    labelText,
    inputName,
    showClearBtn,
    submitType,
    submitText,
    placeholder,
    autofocus,
    resultPage,
    autocompleteLoader,
    autocompleteEmphasis,
    autocompleteTemplates,
}) {
    const { fetchResults, isFetching, searchTerm, setSearchTerm, onKeyUp, onFocus, queryString } = useContext(SearchContext);

    const [isBtnDisabled, setBtnDisabled] = useState(false);

    const formRef = useRef(null);

    const inputId = 'search-input';

    const onSubmitHandler = () => {
        if (!fetchResults) {
            formRef.current.submit();
        } else {
            fetchResults();
        }
    };

    const onChangeValue = (value) => {
        if (setSearchTerm) {
            setSearchTerm(value);
        }
    };

    let labelFunctionalClassName = `${styles.searchBoxLabelText} search-box-label--text`;

    if (labelType === 'icon') {
        labelFunctionalClassName = `${styles.searchBoxLabelIcon} search-box-label--icon`;
    }

    const onFetchHandler = async (event) => {
        const urlParams = parseURL(queryString?.replace(/query=.*?&|query=.*?$/g, ''));
        const newParams = buildParams({ ...urlParams, query: searchTerm }, true, true);

        fetchResults(event, newParams);
    };

    useEffect(() => {
        if (isFetching || searchTerm === '') {
            setBtnDisabled(true);
        } else {
            setBtnDisabled(false);
        }
    }, [isFetching, searchTerm]);

    return (
        <>
            <form
                ref={formRef}
                data-emphasis={autocompleteEmphasis}
                onSubmit={onFetchHandler}
                action={resultPage}
                className={`search-box__form ${styles.searchBoxForm} no-wysiwyg`}
            >
                <AssistedInputContainer
                    id={inputId}
                    onSubmit={onFetchHandler}
                    alwaysShowResults={false}
                    className={{
                        className: `search-box-container`,
                        cssModule: { 'search-box-container': styles.searchBox, 'search-box-container__announce': styles.searchBoxAnnounce },
                        replaceBaseClass: true,
                        retainMissingModuleClasses: true,
                    }}
                >
                    <AssistedInputLabel
                        showLoader={autocompleteLoader}
                        className={{
                            className: labelType === 'text' && labelText === '' ? 'sr-only' : 'search-box-label',
                            cssModule: {
                                'search-box-label': labelFunctionalClassName,
                                'search-box-label__loader': styles.searchBoxLoader,
                            },
                            replaceBaseClass: !(labelType === 'text' && labelText === ''),
                            retainMissingModuleClasses: true,
                        }}
                    >
                        {labelType === 'icon' && (
                            <svg className={`search-box__icon ${styles.searchBoxIcon}`} aria-hidden="true">
                                <title>{placeholder}</title>
                                <use href="#search" />
                            </svg>
                        )}

                        {labelType === 'text' &&
                            (labelText !== '' ? <span>{labelText}</span> : <span className="sr-only">{placeholder}</span>)}
                    </AssistedInputLabel>
                    <AssistedInput
                        name={inputName}
                        onKeyUp={onKeyUp}
                        onFocus={onFocus}
                        onChangeValue={onChangeValue}
                        placeholder={placeholder}
                        showClear={showClearBtn}
                        clearIcon={
                            <svg className={`search-box__icon ${styles.searchBoxIcon}`}>
                                <title>Clear search</title>
                                <use href="#close" />
                            </svg>
                        }
                        autofocus={autofocus}
                        preloadValueFromNameParam
                        className={{
                            className: `search-box-input`,
                            cssModule: {
                                'search-box-input': `${styles.searchBoxInput} search-box-input`,
                                'search-box-input__clear': `${styles.searchBoxClear} search-box-input__clear`,
                                'search-box-input__clear-label': styles.searchBoxClearLabel,
                            },
                            replaceBaseClass: true,
                            retainMissingModuleClasses: true,
                        }}
                    />

                    {autocompleteTemplates && autocompleteTemplates.length > 0 && (
                        <AssistedInputResultsContainer
                            className={{
                                className: 'search-box-results',
                                cssModule: {
                                    'search-box-results': `${styles.searchBoxResults} search-box-results`,
                                },
                                replaceBaseClass: true,
                                retainMissingModuleClasses: true,
                            }}
                        >
                            {autocompleteTemplates.map((item, index) => (
                                <AssistedInputSourceFunnelback
                                    key={`${item.id}-${item.heading}-${index}`}
                                    id={item.id}
                                    heading={item.heading}
                                    source={item.source}
                                    generateItem={item.generateItemFunc}
                                    onSubmit={onSubmitHandler}
                                    className={{
                                        className: 'search-box-source',
                                        cssModule: {
                                            'search-box-source': `${styles.searchBoxSource} search-box-source`,
                                            'search-box-source__heading': `${styles.searchBoxSourceHeading} search-box-source__heading`,
                                            'search-box-source--visible': `${styles.searchBoxSourceVisible} search-box-source--visible`,
                                        },
                                        replaceBaseClass: true,
                                        retainMissingModuleClasses: true,
                                    }}
                                />
                            ))}
                        </AssistedInputResultsContainer>
                    )}
                </AssistedInputContainer>
                <button
                    type="submit"
                    className={`search-box__submit ${styles.searchBoxSubmit}`}
                    disabled={isBtnDisabled}
                    label="Perform search"
                    onSubmit={onSubmitHandler}
                >
                    {submitType === 'text' && submitText !== '' ? (
                        submitText
                    ) : (
                        <svg className={`search-box__icon ${styles.searchBoxIcon}`}>
                            <title>Perform search</title>
                            <use href="#search" />
                        </svg>
                    )}
                </button>
            </form>
            <SearchInfoTemplate resultPage={resultPage} />
        </>
    );
}

/**
 * Prop Types for SearchBox.
 *
 * @type {Object}
 */

SearchBox.propTypes = {
    labelType: PropTypes.oneOf(['icon', 'text']),
    labelText: PropTypes.string,
    inputName: PropTypes.string,
    showClearBtn: PropTypes.bool,
    submitType: PropTypes.oneOf(['icon', 'text']),
    submitText: PropTypes.string,
    placeholder: PropTypes.string,
    autofocus: PropTypes.bool,
    resultPage: PropTypes.string,
    autocompleteLoader: PropTypes.bool,
    autocompleteEmphasis: PropTypes.oneOf(['none', 'query', 'notquery']),
    autocompleteTemplates: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            heading: PropTypes.string,
            source: PropTypes.shape({
                url: PropTypes.string.isRequired,
                params: PropTypes.shape({
                    fmt: PropTypes.string,
                    alpha: PropTypes.number,
                    show: PropTypes.number,
                    collection: PropTypes.string.isRequired,
                    profile: PropTypes.string,
                }).isRequired,
            }).isRequired,
            generateItemFunc: PropTypes.func.isRequired,
        })
    ),
};

SearchBox.defaultProps = {
    labelType: 'icon',
    labelText: '',
    inputName: 'query',
    showClearBtn: true,
    submitType: 'icon',
    submitText: '',
    placeholder: 'Search for something...',
    autofocus: true,
    resultPage: './search.html',
    autocompleteLoader: true,
    autocompleteEmphasis: 'query',
    autocompleteTemplates: [
        {
            id: 'fb-he-organic',
            heading: 'Suggestions',
            source: {
                url: 'https://squiz-search.clients.funnelback.com/s/suggest.json',
                params: {
                    fmt: 'json++',
                    alpha: 0.5,
                    show: 5,
                    sort: 0,
                    profile: '_default',
                    collection: 'pnp~sp-higher-education-meta',
                },
            },
            generateItemFunc: generateOrganicSuggestion,
        },
        {
            id: 'fb-he-people',
            heading: 'Peoples',
            source: {
                url: 'https://squiz-search.clients.funnelback.com/s/suggest.json',
                params: {
                    fmt: 'json++',
                    alpha: 0.5,
                    show: 5,
                    sort: 0,
                    profile: 'auto-completion-people',
                    collection: 'pnp~sp-higher-education-meta',
                },
            },
            generateItemFunc: generatePeopleSuggestion,
        },
        {
            id: 'fb-he-programs',
            heading: 'Programs',
            source: {
                url: 'https://squiz-search.clients.funnelback.com/s/suggest.json',
                params: {
                    fmt: 'json++',
                    alpha: 0.5,
                    show: 5,
                    sort: 0,
                    profile: 'auto-completion-programs',
                    collection: 'pnp~sp-higher-education-meta',
                },
            },
            generateItemFunc: generateProgramSuggestion,
        },
    ],
};

export default SearchBox;
